import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { apiGet } from '../../utils/api'
import { fmtDuration } from './ChatHistoryCompaniesView'

interface Att { id: string; url: string; name: string; type: string; size: number }
interface Msg { id: string; sender: string; senderName: string; content: string; messageType: string; createdAt: string; attachments: Att[] }
interface Chat {
  id: string | number
  status: string
  created_at: string
  closed_at: string | null
  visitor_name: string | null
  visitor_email: string | null
  resolution: string | null
  messages: Msg[]
}
interface CompanyRow { alias: string; company_name: string; company_logo_url?: string | null; seconds: number; chats: number; last_chat_at: string | null }
interface AgentBreakdown {
  month: string
  agent: { id: string | number; name: string; avatar_url: string | null }
  total_seconds: number
  total_chats: number
  companies: CompanyRow[]
}
interface CompanyChats { alias: string; company_name: string; company_logo_url?: string | null; total_chats: number; chats: Chat[] }

function fmtShort(v: string): string {
  try { return new Date(v).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) } catch { return v }
}
function monthLabel(ym?: string): string {
  if (!ym) return ''
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  return new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
}

const MessageRow: React.FC<{ m: Msg; visitor: string | null }> = ({ m, visitor }) => {
  const isAgent = m.sender === 'agent'
  const isSystem = m.sender === 'system' || m.sender === 'bot'
  if (isSystem) return <div className="text-center text-[11px] text-txt-tertiary py-1">{m.content}</div>
  const name = isAgent ? m.senderName || 'Agent' : visitor || 'Customer'
  return (
    <div className={`flex flex-col ${isAgent ? 'items-end' : 'items-start'}`}>
      <div className="text-[10px] text-txt-tertiary mb-0.5 px-1">{name} · {fmtShort(m.createdAt)}</div>
      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${isAgent ? 'bg-brand-500 text-white' : 'bg-surface border border-border text-txt-primary'}`}>
        {m.messageType === 'html' ? <div dangerouslySetInnerHTML={{ __html: m.content }} /> : m.content}
        {m.attachments && m.attachments.length > 0 && (
          <div className="mt-2 flex flex-col gap-2">
            {m.attachments.map((a) => a.type === 'image'
              ? <a key={a.id} href={a.url} target="_blank" rel="noreferrer"><img src={a.url} alt={a.name} className="max-w-[220px] max-h-[220px] rounded-lg border border-black/5" /></a>
              : <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="text-xs underline break-all">📎 {a.name}</a>)}
          </div>
        )}
      </div>
    </div>
  )
}

export const AgentDetail: React.FC = () => {
  const { agentId } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const month = params.get('month') || undefined

  const [data, setData] = useState<AgentBreakdown | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selected, setSelected] = useState<string | null>(null)
  const [transcripts, setTranscripts] = useState<CompanyChats | null>(null)
  const [tLoading, setTLoading] = useState(false)
  const [expanded, setExpanded] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    const q = month ? `?month=${encodeURIComponent(month)}` : ''
    apiGet<AgentBreakdown>(`/chat-history/agents/${encodeURIComponent(agentId || '')}${q}`)
      .then((d) => { if (active) { setData(d); setError(null); if (d.companies?.[0]) setSelected(d.companies[0].alias) } })
      .catch(() => { if (active) setError('Failed to load the agent report') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [agentId, month])

  useEffect(() => {
    if (!selected) { setTranscripts(null); return }
    let active = true
    setTLoading(true)
    const q = month ? `?month=${encodeURIComponent(month)}` : ''
    apiGet<CompanyChats>(`/chat-history/agents/${encodeURIComponent(agentId || '')}/companies/${encodeURIComponent(selected)}${q}`)
      .then((d) => { if (active) { setTranscripts(d); setExpanded(d.chats?.[0] ? String(d.chats[0].id) : null) } })
      .catch(() => { if (active) setTranscripts(null) })
      .finally(() => { if (active) setTLoading(false) })
    return () => { active = false }
  }, [agentId, selected, month])

  const agent = data?.agent

  return (
    <div className="h-full overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b border-border shrink-0">
        <button onClick={() => navigate(-1)} title="Back" className="flex items-center justify-center w-8 h-8 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
        </button>
        {agent?.avatar_url ? (
          <img src={agent.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
        ) : (
          <div className="w-9 h-9 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold">{(agent?.name || '?').charAt(0).toUpperCase()}</div>
        )}
        <div className="min-w-0">
          <h1 className="text-base font-semibold text-txt-primary truncate">{agent?.name || 'Agent'}</h1>
          <p className="text-xs text-txt-tertiary">{monthLabel(data?.month || month)} · {fmtDuration(data?.total_seconds)} · {data?.total_chats ?? 0} chats</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-txt-tertiary p-6">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-500 p-6">{error}</p>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(300px,400px)_1fr]">
          {/* Left: per-company table */}
          <div className="min-h-0 overflow-y-auto border-r border-border">
            {(data?.companies || []).length === 0 ? (
              <p className="text-sm text-txt-tertiary p-6">No chats answered this month.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-surface-secondary">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-txt-tertiary">
                    <th className="px-4 py-2.5 font-semibold">Company</th>
                    <th className="px-3 py-2.5 font-semibold text-right">Time</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Chats</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(data?.companies || []).map((c) => (
                    <tr
                      key={c.alias}
                      onClick={() => setSelected(c.alias)}
                      className={`cursor-pointer transition-colors ${selected === c.alias ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-surface-hover'}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {c.company_logo_url ? (
                            <img src={c.company_logo_url} alt="" className="w-6 h-6 rounded object-contain bg-white border border-border shrink-0" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }} />
                          ) : (
                            <div className="w-6 h-6 rounded bg-brand-100 text-brand-700 flex items-center justify-center text-[10px] font-semibold shrink-0">{(c.company_name || '?').charAt(0).toUpperCase()}</div>
                          )}
                          <span className="truncate text-txt-primary" title={c.company_name}>{c.company_name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums font-medium text-txt-primary">{fmtDuration(c.seconds)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-txt-secondary">{c.chats}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Right: split-screen transcripts for the selected company */}
          <div className="min-h-0 overflow-y-auto bg-surface-secondary">
            {!selected ? (
              <p className="text-sm text-txt-tertiary p-6">Select a company to see the transcripts.</p>
            ) : tLoading ? (
              <p className="text-sm text-txt-tertiary p-6">Loading transcripts…</p>
            ) : !transcripts || transcripts.chats.length === 0 ? (
              <p className="text-sm text-txt-tertiary p-6">No transcripts for this company.</p>
            ) : (
              <div className="p-4 space-y-3">
                <div className="text-sm font-semibold text-txt-primary px-1">{transcripts.company_name} · {transcripts.total_chats} chat{transcripts.total_chats === 1 ? '' : 's'}</div>
                {transcripts.chats.map((c) => {
                  const open = expanded === String(c.id)
                  return (
                    <div key={c.id} className="rounded-xl border border-border bg-surface overflow-hidden">
                      <button onClick={() => setExpanded(open ? null : String(c.id))} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-hover transition-colors text-left">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-txt-primary truncate">{c.visitor_name || c.visitor_email || 'Visitor'}</div>
                          <div className="text-[11px] text-txt-tertiary">{fmtShort(c.created_at)} · {c.status}{c.resolution ? ` · ${c.resolution}` : ''}</div>
                        </div>
                        <svg className={`w-4 h-4 text-txt-tertiary transition-transform shrink-0 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                      </button>
                      {open && (
                        <div className="px-4 py-3 border-t border-border flex flex-col gap-2 bg-surface-secondary/40">
                          {c.messages.length === 0 ? (
                            <p className="text-xs text-txt-tertiary">No messages.</p>
                          ) : (
                            c.messages.map((m) => <MessageRow key={m.id} m={m} visitor={c.visitor_name || c.visitor_email} />)
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
